import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Loading } from "../../../../../components/Loading";
import { BrComerceApiService } from "../../../../../services/BrComerceApiService.js";
import store from "../../../../../store/store";
import styles from "./styles.module.scss";
import DeliveryTypeEnum from "../../../../../enums/DeliveryTypeEnum.js";

// CNPJ helpers
const onlyDigits = (s) => String(s ?? "").replace(/\D/g, "");

const formatCnpj = (s) => {
  const d = onlyDigits(s).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const isValidCnpj = (raw) => {
  const cnpj = onlyDigits(raw);
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1+$/.test(cnpj)) return false;

  const calcDigit = (slice, weights) =>
    slice.split("").reduce((acc, ch, i) => acc + Number(ch) * weights[i], 0);

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const d1Sum = calcDigit(cnpj.slice(0, 12), w1);
  const d1 = ((d1Sum % 11) < 2) ? 0 : 11 - (d1Sum % 11);
  if (Number(cnpj[12]) !== d1) return false;

  const d2Sum = calcDigit(cnpj.slice(0, 13), w2);
  const d2 = ((d2Sum % 11) < 2) ? 0 : 11 - (d2Sum % 11);
  return Number(cnpj[13]) === d2;
};

const Proceda = () => {
  const api = new BrComerceApiService();

  const { auth: { user } } = store.getState();

  // CNPJs do embarcador — texto único separado por vírgula (ex: "00.000.000/0000-00, 11.111.111/1111-11")
  const [cnpjsInput, setCnpjsInput] = useState("");

  const [loading, setLoading] = useState(false);
  const [activeInsurance, setActiveInsurance] = useState(false);

  const [activeCreateOrder, setActiveCreateOrder] = useState(true);

  const [sedex, setSedex] = useState("");
  const [sedexOK, setSedexOK] = useState("");

  const [pac, setPac] = useState("");
  const [pacOK, setPacOK] = useState("");

  const [mini, setMini] = useState("");
  const [miniOK, setMiniOK] = useState("");

  const [ownDelivery, setOwnDelivery] = useState("");
  const [ownDeliveryOK, setOwnDeliveryOK] = useState("");


  const canCollect = Boolean(user.canCreateCollectSchedule);

  const handleSubmit = useCallback(
    async (key, value) => {
      return api.makeHttpRequest({
        url: "/proceda/config",
        method: "POST",
        data: {
          key: key,
          value: value
        }
      });
    },
    [api]
  );

  const handleCnpjsChange = useCallback((e) => {
    setCnpjsInput(e.currentTarget.value);
  }, []);

  // Ao sair do campo, reformata os CNPJs válidos para "00.000.000/0000-00, ..."
  const handleCnpjsBlur = useCallback(() => {
    setCnpjsInput((prev) =>
      prev
        .split(",")
        .map((token) => onlyDigits(token))
        .filter((digits) => digits.length > 0)
        .map((digits) => (digits.length === 14 ? formatCnpj(digits) : digits))
        .join(", ")
    );
  }, []);

  const handleInputService = useCallback((e, service) => {
    const valueNumber = e.currentTarget.value.replace(/\D/g, "");
    if (valueNumber > 99) {
      return;
    } else {
      switch (service) {
        case DeliveryTypeEnum.SEDEX:
          if (valueNumber === "") {
            setSedex("");
            setSedexOK("vazio");
          } else {
            setSedex(valueNumber);
            setSedexOK("ok");
          }
          break;
        case DeliveryTypeEnum.PAC:
          if (valueNumber === "") {
            setPac("");
            setPacOK("vazio");
          } else {
            setPac(valueNumber);
            setPacOK("ok");
          }
          break;
        case DeliveryTypeEnum.PACMINI:
          if (valueNumber === "") {
            setMini("");
            setMiniOK("vazio");
          } else {
            setMini(valueNumber);
            setMiniOK("ok");
          }
          break;
        case DeliveryTypeEnum.OWN_DELIVERY:
          if (valueNumber === "") {
            setOwnDelivery("");
            setOwnDeliveryOK("vazio");
          } else {
            setOwnDelivery(valueNumber);
            setOwnDeliveryOK("ok");
          }
          break;
        default:
          return;
      }
    }
  }, []);

  // CNPJs preenchidos (descarta vazios), em dígitos
  const parsedCnpjs = cnpjsInput
    .split(",")
    .map((token) => onlyDigits(token))
    .filter((digits) => digits.length > 0);

  const invalidCnpjs = parsedCnpjs.filter((digits) => !isValidCnpj(digits));
  const hasAtLeastOneCnpj = parsedCnpjs.length > 0;
  const allCnpjsValid = invalidCnpjs.length === 0;

  const sendData = async () => {
    // CNPJs únicos e válidos (em dígitos), gravados como lista separada por vírgula
    const cnpjList = [...new Set(parsedCnpjs)];

    if (!cnpjList.length || cnpjList.some((digits) => !isValidCnpj(digits))) {
      return Swal.fire({
        icon: "warning",
        title: "CNPJ inválido",
        text: "Informe ao menos um CNPJ válido (14 dígitos), separando múltiplos por vírgula."
      });
    }

    try {
      setLoading(true);

      // Remove os CNPJs já gravados antes de regravar a lista consolidada,
      // garantindo que a lista atual seja a fonte única de verdade (inclusive remoções).
      const existing = await api.makeHttpRequest({
        method: "GET",
        url: `/proceda/config/list/SENDER_CNPJ`
      });
      if (Array.isArray(existing) && existing.length) {
        await Promise.all(
          existing.map((item) =>
            api.makeHttpRequest({ url: `/proceda/config/${item.id}`, method: "DELETE" })
          )
        );
      }

      await Promise.all([
        handleSubmit("SENDER_CNPJ", cnpjList.join(",")),
        handleSubmit("ADD_DAYS_SEDEX", sedex),
        handleSubmit("ADD_DAYS_PAC", pac),
        handleSubmit("ADD_DAYS_PACMINI", mini),
        handleSubmit("ADD_DAYS_OWN_DELIVERY", ownDelivery),
        activeInsurance
          ? handleSubmit("IS_ACTIVE_INSURANCE", "true")
          : handleSubmit("IS_ACTIVE_INSURANCE", "false"),
        activeCreateOrder
          ? handleSubmit("IS_INACTIVE_ORDER_CREATE", "false")
          : handleSubmit("IS_INACTIVE_ORDER_CREATE", "true"),
      ]);

      Swal.fire({
        title: "Sucesso!",
        text: "Configurações cadastradas com sucesso.",
        icon: "success",
        showCancelButton: false,
        confirmButtonText: "Ok"
      });

      // Recarrega os CNPJs cadastrados
      await loadCnpjs();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Ocorreu um erro!",
        text: "Erro ao cadastrar configurações"
      });
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    window.setPageTitle("Integrações | Proceda");
  }, []);

  const loadCnpjs = useCallback(async () => {
    // Pode existir 1 registro com lista separada por vírgula (novo formato) ou
    // registros antigos com 1 CNPJ cada — o list cobre os dois casos.
    const list = await api.makeHttpRequest({
      method: "GET",
      url: `/proceda/config/list/SENDER_CNPJ`
    });

    const digits = (Array.isArray(list) ? list : [])
      .flatMap((item) => String(item.value ?? "").split(","))
      .map((token) => onlyDigits(token))
      .filter((value) => value.length > 0);

    const unique = [...new Set(digits)];

    setCnpjsInput(
      unique.map((value) => (value.length === 14 ? formatCnpj(value) : value)).join(", ")
    );
  }, [api]);

  const loadingConfig = useCallback(async () => {
    try {
      await Promise.all([
        api
          .makeHttpRequest({
            method: "GET",
            url: `/proceda/config/IS_ACTIVE_INSURANCE`
          })
          .then(active => {
            if (active === "true") {
              setActiveInsurance(true);
            }
          }),
        api
          .makeHttpRequest({
            method: "GET",
            url: `/proceda/config/IS_INACTIVE_ORDER_CREATE`
          })
          .then(inactive => {
            if (inactive === "true") {
              setActiveCreateOrder(false);
            }
          }),
        loadCnpjs(),
        api
          .makeHttpRequest({
            method: "GET",
            url: `/proceda/config/ADD_DAYS_PACMINI`
          })
          .then(res => {
            if (res) {
              setMini(res);
              setMiniOK("ok");
            }
          }),
        api
          .makeHttpRequest({
            method: "GET",
            url: `/proceda/config/ADD_DAYS_PAC`
          })
          .then(res => {
            if (res) {
              setPac(res);
              setPacOK("ok");
            }
          }),
        api
          .makeHttpRequest({
            method: "GET",
            url: `/proceda/config/ADD_DAYS_SEDEX`
          })
          .then(res => {
            if (res) {
              setSedex(res);
              setSedexOK("ok");
            }
          }),
        api
          .makeHttpRequest({
            method: "GET",
            url: `/proceda/config/ADD_DAYS_OWN_DELIVERY`
          })
          .then(res => {
            if (res) {
              setOwnDelivery(res);
              setOwnDeliveryOK("ok");
            }
          }),
      ]);
    } catch (error) {
      console.log("🚀 ~ file: index.jsx:522 ~ loadingConfig ~ error:", error);
    }
  }, [api, loadCnpjs]);


  useEffect(() => {
    setLoading(true);

    loadingConfig()
      .then(() => {
        console.log("Configurações ok");
      })
      .catch(() => {
        Swal.fire({
          title: "Erro!",
          text:
            "Não foi possível carregar as informações da loja da Proceda.",
          icon: "error"
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <>
      <Loading loading={loading} />
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.contentHeader}>
            <h2>Configurações</h2>
          </div>
          <div className={styles.contentBody}>
            <section>
              <div className={styles.wrapper}>
                <p>
                  CNPJs do embarcador (identificam suas notas fiscais nos arquivos NotFis)
                </p>
                <p style={{ fontSize: 12 }}>
                  Informe um ou mais CNPJs separados por vírgula.
                </p>
                <div
                  className={styles.input}
                  role={invalidCnpjs.length ? "notOk" : (hasAtLeastOneCnpj ? "ok" : "")}
                >
                  <textarea
                    name="cnpjs"
                    value={cnpjsInput}
                    onChange={handleCnpjsChange}
                    onBlur={handleCnpjsBlur}
                    placeholder="00.000.000/0000-00, 11.111.111/1111-11"
                    rows={3}
                    autoComplete="off"
                  />
                </div>
                {invalidCnpjs.length > 0 && (
                  <p className={styles.error}>
                    CNPJ(s) inválido(s):{" "}
                    {invalidCnpjs
                      .map((digits) => (digits.length === 14 ? formatCnpj(digits) : digits))
                      .join(", ")}
                  </p>
                )}
              </div>

              <div className={styles.wrapper}>
                <h4>Criação de pedido</h4>
                <p>
                  Abaixo é possível ativar ou desatitvar a criação de pedidos.
                  <br />
                  Serviços como cotação funcionarão normalmente
                </p>
                <div>
                  <div className={styles.switchOption}>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        onChange={event =>
                          setActiveCreateOrder(event.target.checked)
                        }
                        checked={activeCreateOrder}
                      />
                      <span
                        className={`${styles.slider} ${styles.round}`}
                      ></span>
                    </label>
                    {activeCreateOrder ? "Ativado" : "Desativado"}
                  </div>
                </div>
              </div>

              <div className={styles.wrapper}>
                <h4>Configuração de Seguro</h4>
                <p>
                  Abaixo é possível ativar ou desatitvar a cotação de frete com
                  Seguro. Essa opção é válida para todos os fretes <br /> que
                  são cotados.
                </p>
                <div>
                  <div className={styles.switchOption}>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        onChange={event =>
                          setActiveInsurance(event.target.checked)
                        }
                        checked={activeInsurance}
                      />
                      <span
                        className={`${styles.slider} ${styles.round}`}
                      ></span>
                    </label>
                    {activeInsurance ? "Ativado" : "Desativado"}
                  </div>
                </div>
              </div>

              <div className={styles.wrapper}>
                <h4>Configurações adicionais de frete</h4>
                <p>
                  Abaixo é possível indicar a quantidade de dias adicionais para
                  cada tipo de envio.
                  <br />
                  Essa opção é particularmente útil para refletir nas cotações o
                  seu prazo de manipulação dos pedidos.
                </p>
                <div style={{ marginTop: 0 }} className={styles.contentSection}>
                  <div className={styles.addDaysContainer}>
                    {canCollect && (
                      <div
                        className={`${styles.input} ${ownDeliveryOK === "notOK"
                          ? styles.formSelectError
                          : ownDeliveryOK === "ok"
                            ? styles.selectOk
                            : ""
                          }`}
                      >
                        <input
                          type="number"
                          name="ownDelivery"
                          value={ownDelivery}
                          onChange={(e) => handleInputService(e, DeliveryTypeEnum.OWN_DELIVERY)}
                          placeholder="Super Expressa"
                          min={0}
                          max={99}
                        />
                      </div>
                    )}
                    <div
                      className={`${styles.input} ${sedexOK === "notOK"
                        ? styles.formSelectError
                        : sedexOK === "ok"
                          ? styles.selectOk
                          : ""
                        }`}
                    >
                      <input
                        type="number"
                        name="sedex"
                        value={sedex}
                        onChange={(e) => handleInputService(e, DeliveryTypeEnum.SEDEX)}
                        placeholder="Expresso"
                        min={0}
                        max={99}
                      />
                    </div>
                    <div
                      className={`${styles.input} ${pacOK === "notOK"
                        ? styles.formSelectError
                        : pacOK === "ok"
                          ? styles.selectOk
                          : ""
                        }`}
                    >
                      <input
                        type="number"
                        name="pac"
                        value={pac}
                        onChange={(e) => handleInputService(e, DeliveryTypeEnum.PAC)}
                        placeholder="Econômico"
                        min={0}
                        max={99}
                      />
                    </div>
                    <div
                      className={`${styles.input} ${miniOK === "notOK"
                        ? styles.formSelectError
                        : miniOK === "ok"
                          ? styles.selectOk
                          : ""
                        }`}
                    >
                      <input
                        type="number"
                        name="mini"
                        value={mini}
                        onChange={(e) => handleInputService(e, DeliveryTypeEnum.PACMINI)}
                        placeholder="Super Econômico"
                        min={0}
                        max={99}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.buttonWrapper}>
                <button
                  type="submit"
                  onClick={sendData}
                  className={styles.button}
                  disabled={!hasAtLeastOneCnpj || !allCnpjsValid}
                >
                  Salvar
                </button>
              </div>
            </section>
          </div>
        </div >
      </div >
    </>
  );
};

export { Proceda };
